// The Customers and suppliers page (docs/cockpit/spec.md): every Counterparty the business sells to, buys from
// or sends job work to, by name, filtered by kind and searched by the server. "Add" and each row's "Edit" open the
// same form chat's drafts use, behind the same commands and Roles (#502). Each row's "Files" lists the files kept
// against it (#575).
import { useCallback, useState } from "react";
import { clearing, type Api, type CounterpartyKind, type CounterpartyRow } from "../api.js";
import { Dialog } from "../Dialog.js";
import { useWords, type Words } from "../i18n/words.js";
import { telHref } from "../runs/panel.js";
import { ListSearch, ListView, Segments, useList } from "../lists/List.js";
import { KeptFiles } from "../files/KeptFiles.js";
import { MasterDataForm } from "./MasterDataForm.js";
import { TaxParticularsForm } from "./TaxParticularsForm.js";

type Kind = CounterpartyKind | "all";
const kinds = (w: Words): readonly (readonly [Kind, string])[] => [
  ["all", w.partiesAll],
  ["customer", w.partiesCustomers],
  ["supplier", w.partiesSuppliers],
  ["job_worker", w.partiesJobWorkers],
];
const kindNames = (w: Words): Readonly<Record<string, string>> => ({
  customer: w.partiesCustomer,
  supplier: w.partiesSupplier,
  job_worker: w.partiesJobWorker,
});

export function Parties({
  api,
  initialQuery = "",
}: {
  /**
   * Without `saveCounterparty`, the page only lists; without `counterpartyTaxParticulars`, no tax particulars;
   * without `keptFiles`, no files.
   */
  api: Pick<Api, "counterparties"> &
    Partial<Pick<Api, "saveCounterparty" | "counterpartyTaxParticulars" | "keptFiles">>;
  initialQuery?: string;
}) {
  const w = useWords();
  const names = kindNames(w);
  const [kind, setKind] = useState<Kind>("all");
  const [q, setQ] = useState(initialQuery);
  // Saving reads the list again: a new loader each time.
  const [version, setVersion] = useState(0);
  const load = useCallback<Api["counterparties"]>((filters) => api.counterparties(filters), [api, version]);
  const { loaded, setPage } = useList(load, kind === "all" ? { q } : { kind, q });
  const [editing, setEditing] = useState<CounterpartyRow | "new">();
  const [filesOf, setFilesOf] = useState<CounterpartyRow>();
  const save = api.saveCounterparty;
  const taxParticulars = api.counterpartyTaxParticulars;
  const keptFiles = api.keptFiles;
  return (
    <>
      <div className="hello">
        <h2>{w.partiesTitle}</h2>
        <p className="muted">{w.partiesIntro}</p>
      </div>
      <div className="toolbar">
        <Segments label={w.partiesShow} options={kinds(w)} value={kind} onChange={setKind} />
        <ListSearch label={w.partiesSearch} initial={initialQuery} onSearch={setQ} />
        {save ? (
          <button type="button" className="btn" onClick={() => setEditing("new")}>
            {w.masterAdd}
          </button>
        ) : null}
      </div>
      <ListView loaded={loaded} what={w.partiesWhat} empty={q ? w.partiesNoMatch(q) : w.partiesNone} onPage={setPage}>
        {(rows) => (
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{w.partiesName}</th>
                <th scope="col">{w.partiesKind}</th>
                <th scope="col">{w.partiesGstin}</th>
                <th scope="col">{w.partiesPhone}</th>
                <th scope="col" className="r">
                  {w.partiesPaysIn}
                </th>
                {save || keptFiles ? (
                  <th scope="col">
                    <span className="sr-only">{w.masterActions}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <th scope="row">{row.name}</th>
                  <td>{names[row.kind] ?? row.kind}</td>
                  <td>{row.gstin ?? "—"}</td>
                  <td>{row.phone ? <a href={telHref(row.phone)}>{row.phone}</a> : "—"}</td>
                  <td className="r">{row.paymentTermsDays === null ? "—" : w.partiesDays(row.paymentTermsDays)}</td>
                  {save || keptFiles ? (
                    <td>
                      {save ? (
                        <button
                          type="button"
                          className="btn"
                          aria-label={w.masterEditOf(row.name)}
                          onClick={() => setEditing(row)}
                        >
                          {w.masterEdit}
                        </button>
                      ) : null}
                      {keptFiles ? (
                        <button
                          type="button"
                          className="btn"
                          aria-label={w.keptFilesOf(row.name)}
                          onClick={() => setFilesOf(row)}
                        >
                          {w.keptFilesButton}
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListView>
      {save && editing ? (
        <Dialog
          title={editing === "new" ? w.masterAddParty : w.masterEditOf(editing.name)}
          onClose={() => setEditing(undefined)}
        >
          <MasterDataForm
            record={editing === "new" ? "customer" : (editing.kind as CounterpartyKind)}
            chooseKind={editing === "new"}
            editing={editing !== "new"}
            initial={
              editing === "new"
                ? {}
                : {
                    name: editing.name,
                    ...(editing.gstin ? { gstin: editing.gstin } : {}),
                    ...(editing.address ? { address: editing.address } : {}),
                    ...(editing.state ? { state: editing.state } : {}),
                    ...(editing.phone ? { phone: editing.phone } : {}),
                    ...(editing.email ? { email: editing.email } : {}),
                    ...(editing.paymentTermsDays === null ? {} : { paymentTermsDays: editing.paymentTermsDays }),
                  }
            }
            submitLabel={w.masterSave}
            onSubmit={async (fields, chosen, emptied) => {
              // Editing, a field the person emptied is cleared on record, not kept.
              const saved = await (editing === "new"
                ? save(chosen, fields)
                : save(editing.kind as CounterpartyKind, { ...fields, ...clearing(emptied) }, editing.id));
              setVersion((v) => v + 1);
              return saved.text;
            }}
          />
          {/* A supplier's or job worker's PAN, usual Nature of Payment and lower-deduction certificate (#617). */}
          {editing !== "new" && editing.kind !== "customer" && taxParticulars ? (
            <TaxParticularsForm
              api={{ counterpartyTaxParticulars: taxParticulars, saveCounterparty: save }}
              counterparty={{ id: editing.id, name: editing.name, kind: editing.kind as CounterpartyKind }}
            />
          ) : null}
        </Dialog>
      ) : null}
      {keptFiles && filesOf ? (
        <Dialog title={w.keptFilesOf(filesOf.name)} onClose={() => setFilesOf(undefined)}>
          <KeptFiles
            api={{ keptFiles }}
            object={filesOf.kind === "customer" ? "customer" : "supplier"}
            record={filesOf.id}
          />
        </Dialog>
      ) : null}
    </>
  );
}
